// Contrato: suggest(texto, { signal }) => Promise<Array<{
// id, label, address, comuna, region, lat, lng }>>. Campos ausentes: null.
// Solo direcciones de Chile.
export const normalizeText = (text) =>
  (text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

export const mock = {
  attribution: null,
  async suggest(text, { signal } = {}) {
    if (signal?.aborted) throw new DOMException('Cancelado', 'AbortError');
    return [
      {
        id: 'apoquindo',
        label: 'Av. Apoquindo 4501, Las Condes',
        address: 'Av. Apoquindo 4501',
        comuna: 'Las Condes',
        region: 'Región Metropolitana de Santiago',
        lat: -33.414,
        lng: -70.584,
      },
      {
        id: 'alameda',
        label: 'Alameda 1000, Santiago',
        address: 'Alameda 1000',
        comuna: 'Santiago',
        region: 'Región Metropolitana de Santiago',
        lat: -33.443,
        lng: -70.65,
      },
    ].filter((item) => normalizeText(item.label).includes(normalizeText(text)));
  },
};

export const photon = {
  attribution: {
    label: '© OpenStreetMap contributors',
    url: 'https://www.openstreetmap.org/copyright',
  },
  async suggest(text, { signal } = {}) {
    const url = new URL('https://photon.komoot.io/api/');
    url.searchParams.set('q', text);
    url.searchParams.set('limit', '8');
    // bbox de Chile continental sin verificar contra la API real.
    url.searchParams.set('bbox', '-75.7,-56.0,-66.4,-17.5');
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error('No se pudo buscar');
    const data = await response.json();
    // Forma de la respuesta sin verificar contra la API real.
    return (data.features || [])
      .filter((f) => f.properties?.countrycode?.toUpperCase() === 'CL')
      .map((f, index) => {
        const p = f.properties;
        const address =
          [p.street || p.name, p.housenumber].filter(Boolean).join(' ') || null;
        const comuna = p.city || p.district || p.county || null;
        return {
          id: String(p.osm_id ?? index),
          label: [address, comuna, p.state].filter(Boolean).join(', '),
          address,
          comuna,
          region: p.state || null,
          lat: f.geometry?.coordinates?.[1] ?? null,
          lng: f.geometry?.coordinates?.[0] ?? null,
        };
      })
      .filter((item) => item.address);
  },
};

// Maps JavaScript API se carga una sola vez por ventana, con su callback e
// importLibrary. Es la librería pensada para el navegador: la key va
// restringida por referrer (HTTP) en Google Cloud, a diferencia de Places API
// (New) por HTTP, que es un web service y pide restricción por IP o un proxy.
const loaded = new WeakMap();
export function loadPlacesLibrary(key, { doc = document, win = window } = {}) {
  if (loaded.has(win)) return loaded.get(win);
  const library = new Promise((resolve, reject) => {
    if (win.google?.maps?.importLibrary) {
      resolve(win.google.maps.importLibrary('places'));
      return;
    }
    const callback = '__recepthorMapsReady';
    win[callback] = () => {
      delete win[callback];
      resolve(win.google.maps.importLibrary('places'));
    };
    const script = doc.createElement('script');
    const params = new URLSearchParams({
      key,
      v: 'weekly',
      loading: 'async',
      language: 'es',
      region: 'CL',
      callback,
    });
    script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
    script.async = true;
    script.onerror = () => {
      script.remove?.();
      reject(new Error('No se pudo cargar Google Maps'));
    };
    doc.head.appendChild(script);
  });
  // Un fallo de carga, del script o de importLibrary, se puede reintentar en
  // la próxima búsqueda.
  library.catch(() => loaded.delete(win));
  loaded.set(win, library);
  return library;
}

const abortError = () => new DOMException('Cancelado', 'AbortError');

export function createGoogleAdapter(
  key,
  { loadPlaces = () => loadPlacesLibrary(key) } = {},
) {
  // Una sesión de autocompletar dura desde la primera búsqueda hasta elegir
  // una dirección: Google cobra la sesión completa y no cada tecla.
  let sessionToken;
  // Las predicciones de la última búsqueda, para pedir el detalle del lugar
  // elegido con su misma sesión.
  let predictions = new Map();
  return {
    // Atribución textual; revisar si los términos exigen logo.
    attribution: {
      label: 'Powered by Google',
      url: 'https://maps.google.com/help/terms_maps/',
    },
    async suggest(input, { signal } = {}) {
      if (signal?.aborted) throw abortError();
      const places = await loadPlaces();
      // La primera búsqueda espera la carga del script: lo que se tecleó
      // mientras tanto llega abortado y no se paga.
      if (signal?.aborted) throw abortError();
      if (!sessionToken) sessionToken = new places.AutocompleteSessionToken();
      let suggestions;
      try {
        ({ suggestions } =
          await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input,
            includedRegionCodes: ['cl'],
            language: 'es',
            region: 'cl',
            sessionToken,
          }));
      } catch {
        throw new Error('No se pudo buscar');
      }
      // La librería no acepta AbortSignal: una búsqueda abortada se descarta
      // al volver.
      if (signal?.aborted) throw abortError();
      predictions = new Map();
      return (suggestions || []).flatMap(({ placePrediction: p }) => {
        const address = p?.mainText?.text;
        if (!p?.placeId || !address) return [];
        predictions.set(p.placeId, p);
        return [
          {
            id: p.placeId,
            label: p.text?.text || address,
            address,
            comuna: null,
            region: null,
            lat: null,
            lng: null,
          },
        ];
      });
    },
    // El adaptador vive más que el formulario: una sesión abandonada no se
    // reutiliza en la siguiente.
    reset() {
      sessionToken = undefined;
      predictions = new Map();
    },
    async resolve(suggestion, { signal } = {}) {
      if (signal?.aborted) throw abortError();
      const places = await loadPlaces();
      if (signal?.aborted) throw abortError();
      const prediction = predictions.get(suggestion.id);
      // Elegir cierra la sesión: la siguiente búsqueda abre una nueva.
      sessionToken = undefined;
      predictions = new Map();
      // toPlace() conserva la sesión de la búsqueda; sin predicción se pide el
      // lugar por id, fuera de sesión.
      const place = prediction
        ? prediction.toPlace()
        : new places.Place({ id: suggestion.id });
      try {
        await place.fetchFields({ fields: ['addressComponents', 'location'] });
      } catch {
        throw new Error('No se pudo buscar');
      }
      if (signal?.aborted) throw abortError();
      const component = (type) =>
        place.addressComponents?.find((c) => c.types?.includes(type))
          ?.longText || null;
      return {
        ...suggestion,
        comuna:
          component('administrative_area_level_3') || component('locality'),
        region: component('administrative_area_level_1'),
        lat: place.location?.lat() ?? null,
        lng: place.location?.lng() ?? null,
      };
    },
  };
}

export function getAddressAdapter(provider, { key } = {}) {
  if (provider === 'google' && key?.trim())
    return createGoogleAdapter(key.trim());
  // Photon y mock son solo para pruebas locales: no se entregan en producción.
  if (process.env.NODE_ENV === 'production') return null;
  if (provider === 'photon') return photon;
  if (provider === 'mock') return mock;
  return null;
}

export function proposeComuna(suggestion, current, catalog) {
  if (current.trim() || !suggestion.comuna) return null;
  const matches = catalog.filter(
    (item) => normalizeText(item.comuna) === normalizeText(suggestion.comuna),
  );
  return matches.length === 1 ? matches[0] : null;
}
